import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'
import { fetchTerritoryBoundaries, fetchTerritorySummaries } from '../api'
import { selectIdsToFetch, ViewportBounds } from '../boundsOverlap'
import {
  selectTerritoryBoundariesById,
  selectTerritoryLoadingIds,
  selectTerritorySummaries,
  selectTerritorySummariesStatus,
} from '../slice'

/**
 * Keeps the territory layer fed (WWM-17508).
 *
 * Two fetches with deliberately different shapes:
 *
 *  1. Summaries — once, on mount. Every territory's name, colour and bounds, no geometry.
 *  2. Geometry — only for territories the viewport touches, and only once each.
 *
 * The split is what keeps this cheap. Measured on 24 territories of ~600 vertices, fetching all
 * geometry was 135 KB gzipped against 29 KB for summaries-plus-visible, and the gap widens with
 * tenant size because step 2 scales with what is on screen rather than with how many territories
 * exist. Geometry is cached by id and never re-fetched, since territory polygons are static
 * between edits — panning back costs nothing, unlike the viewport-driven pin feed.
 */
export function useTerritoryLayer(viewport: ViewportBounds | null) {
  const dispatch = useAppDispatch()
  const enabled = useD2DTerritoriesEnabled()
  const summaries = useSelector(selectTerritorySummaries)
  const summariesStatus = useSelector(selectTerritorySummariesStatus)
  const boundariesById = useSelector(selectTerritoryBoundariesById)
  const loadingIds = useSelector(selectTerritoryLoadingIds)

  // Step 1: the summary fetch. Normally once, on mount.
  //
  // A failure is retried, because this one request gates the ENTIRE layer -- without summaries
  // there are no bounds, so step 2 never runs and the tenant sees no territories at all. Treating
  // one transient 500 as permanent would silently disable the feature for the rest of the session.
  //
  // The retry is tied to the viewport changing rather than firing off the error state directly:
  // re-dispatching on 'error' would spin, since the rejection re-enters this effect immediately.
  // Keying on the viewport makes each retry user-driven, and therefore naturally rate-limited.
  const retriedForViewportRef = useRef<string | null>(null)
  const viewportKey = viewport
    ? `${viewport.minLatitude},${viewport.maxLatitude},${viewport.minLongitude},${viewport.maxLongitude}`
    : null

  useEffect(() => {
    if (!enabled) return
    if (summariesStatus === 'loading' || summariesStatus === 'loaded') return

    if (summariesStatus === 'error') {
      if (retriedForViewportRef.current === viewportKey) return
      retriedForViewportRef.current = viewportKey
    }

    dispatch(fetchTerritorySummaries())
  }, [dispatch, enabled, summariesStatus, viewportKey])

  // Step 2: fill the geometry cache for whatever the viewport now touches.
  //
  // boundariesById/loadingIds are read through a ref rather than listed as deps: they change on
  // every fulfilled fetch, and depending on them would re-run this effect immediately after each
  // one. selectIdsToFetch already returns [] once everything visible is cached, so it would
  // terminate — but only after a render-loop's worth of no-op passes.
  const cacheRef = useRef({ boundariesById, loadingIds })
  cacheRef.current = { boundariesById, loadingIds }

  useEffect(() => {
    if (!enabled || !viewport || !summaries.length) return

    const idsToFetch = selectIdsToFetch(
      summaries,
      viewport,
      new Set(Object.keys(cacheRef.current.boundariesById).map(Number)),
      new Set(cacheRef.current.loadingIds),
    )

    if (!idsToFetch.length) return

    dispatch(fetchTerritoryBoundaries(idsToFetch))
  }, [dispatch, enabled, viewport, summaries])
}
